/**
 * AEGIS-8 swarm seed — populates aegis_agents + aegis_agent_events +
 * aegis_negotiations for the kairos-demo tenant with:
 *   • 7 named agents from AGENT_DEFS, each with memory snapshots
 *   • 1 fully-resolved stockout negotiation with full transcript
 *   • 1 in-progress negotiation + 1 escalated to human
 *
 * The stockout scenario is the session's feature demo: InventoryAgent
 * detects stockout risk → CarrierAgent proposes expedited freight →
 * FinanceAgent counters on cost → consensus output with attributed
 * rationale from each contributing agent.
 */
import { DEMO_TENANT_ID } from './tenantDefaults';
import { AGENT_DEFS } from './agentRegistry';

const now = new Date();
const minutesAgo = (m) => new Date(now.getTime() - m * 60000).toISOString();
const hoursAgo = (h) => new Date(now.getTime() - h * 3600000).toISOString();

const baseMeta = (extra = {}) => ({
  tenantId: DEMO_TENANT_ID,
  createdAt: hoursAgo(2),
  createdBy: 'aegis_auto',
  updatedAt: minutesAgo(10),
  updatedBy: 'aegis_auto',
  isDeleted: false,
  softDeleted: false,
  version: 1,
  source: 'swarm_seed',
  ...extra,
});

// ── Agent records ──────────────────────────────────────────────────────
export const SEED_AEGIS_AGENTS = AGENT_DEFS.map((def) => ({
  id: def.agentId,
  ...def,
  status: 'active',
  lastActiveAt: minutesAgo(5),
  ...baseMeta({ createdAt: hoursAgo(72) }),
}));

// ── Agent memory snapshots (flat; client stores under subcollection path)
// We emit one combined seed list with an `agentMemoryParent` marker so the
// loader can deposit each row into the agent's memory subcollection
// equivalent in localStorage (`wc_sub_aegis_agents__<id>__memory`).

export const SEED_AGENT_MEMORY = [
  // CarrierAgent
  { agentId: 'carrier_agent', type: 'observation', payload: { carrier: 'SWFT', lane: 'DAL-LAX', note: '2 declines on this lane in last 10 days' }, importance: 0.8, observedAt: hoursAgo(6) },
  { agentId: 'carrier_agent', type: 'observation', payload: { carrier: 'WERN', lane: 'DAL-LAX', note: '5 recent accepts, 95% OTP' }, importance: 0.9, observedAt: hoursAgo(12) },
  { agentId: 'carrier_agent', type: 'fact', payload: { lane: 'DAL-LAX', marketRate: 3200, dataPoints: 24 }, importance: 0.7, observedAt: hoursAgo(18) },
  // InventoryAgent
  { agentId: 'inventory_agent', type: 'observation', payload: { sku: 'SKU-4421', onHand: 320, velocity: 54, leadTime: 8 }, importance: 0.95, observedAt: hoursAgo(4) },
  { agentId: 'inventory_agent', type: 'observation', payload: { sku: 'SKU-4421', projectedStockout: '6 days', riskLevel: 'high' }, importance: 0.95, observedAt: hoursAgo(4) },
  // FinanceAgent
  { agentId: 'finance_agent', type: 'fact', payload: { mtdBudget: 480000, mtdActual: 462000, utilization: 0.96 }, importance: 0.85, observedAt: hoursAgo(8) },
  { agentId: 'finance_agent', type: 'constraint', payload: { note: 'Expedited freight budget for month: $6,500 remaining' }, importance: 0.9, observedAt: hoursAgo(8) },
  // CustomerAgent
  { agentId: 'customer_agent', type: 'observation', payload: { customer: 'Wild Fork Foods', slaTarget: 0.95, currentSla: 0.89, churnRisk: 0.42 }, importance: 0.9, observedAt: hoursAgo(5) },
  // FleetAgent
  { agentId: 'fleet_agent', type: 'observation', payload: { vehicleId: 'fv-004', inspectionDueDays: 2, status: 'active' }, importance: 0.7, observedAt: hoursAgo(3) },
  // WarehouseAgent
  { agentId: 'warehouse_agent', type: 'observation', payload: { zone: 'B', pickPace: 0.72, targetPace: 0.9 }, importance: 0.75, observedAt: hoursAgo(2) },
  // ComplianceAgent
  { agentId: 'compliance_agent', type: 'alert', payload: { driverId: 'fd-003', doc: 'medical_cert', status: 'expired_12d' }, importance: 1.0, observedAt: hoursAgo(6) },
].map(row => ({
  id: `mem-${row.agentId}-${row.observedAt.replace(/[^0-9]/g, '').slice(0, 12)}`,
  agentMemoryParent: `aegis_agents/${row.agentId}/memory`,
  ...row,
  ...baseMeta({ createdAt: row.observedAt }),
}));

// ── Stockout negotiation — the hero demo ───────────────────────────────
const STOCKOUT_THREAD = 'neg-001';

export const SEED_AEGIS_AGENT_EVENTS = [
  // 1) InventoryAgent surfaces the risk
  {
    id: 'evt-001', eventId: 'evt-001', threadId: STOCKOUT_THREAD, stepIndex: 0,
    kind: 'observation',
    fromAgentId: 'inventory_agent', toAgentId: null,
    topic: 'SKU-4421 stockout risk',
    payload: { sku: 'SKU-4421', onHand: 320, velocity: 54, projectedStockout: '6 days', leadTime: 8 },
    confidence: 0.91,
    rationale: 'SKU-4421 will stock out in 6 days at current velocity. Standard lead time is 8 days. Safety stock is already below floor.',
    relatedEntityId: 'SKU-4421', relatedEntityType: 'inventory_item', module: 'ims',
    ...baseMeta({ createdAt: minutesAgo(45), createdBy: 'inventory_agent' }),
  },
  // 2) CarrierAgent proposes expedited air freight
  {
    id: 'evt-002', eventId: 'evt-002', threadId: STOCKOUT_THREAD, stepIndex: 1,
    kind: 'proposal',
    fromAgentId: 'carrier_agent', toAgentId: null,
    topic: 'Expedited air freight',
    payload: { proposal: 'book_expedited_air', supplier: 'sup-001', quantity: 480, carrier: 'UPS Air', estCost: 8200, leadTime: 2 },
    confidence: 0.83,
    rationale: 'Air freight closes the gap in 2 days. I have current UPS Air lane coverage on Chicago → Dallas with historical 97% OTP.',
    relatedEntityId: 'SKU-4421', relatedEntityType: 'inventory_item', module: 'tms',
    ...baseMeta({ createdAt: minutesAgo(40), createdBy: 'carrier_agent' }),
  },
  // 3) FinanceAgent counters on cost
  {
    id: 'evt-003', eventId: 'evt-003', threadId: STOCKOUT_THREAD, stepIndex: 2,
    kind: 'counter_proposal',
    fromAgentId: 'finance_agent', toAgentId: 'carrier_agent',
    topic: 'Cost counter',
    payload: { counter: 'partial_expedite', recommendedQty: 200, estCost: 3400, note: 'Expedite only what covers lead time gap; standard-ship the balance.' },
    confidence: 0.88,
    rationale: '$8,200 expedite exceeds remaining month-end expedite budget ($6,500). Splitting the order holds 60% of urgency at 41% of cost.',
    relatedEntityId: 'SKU-4421', relatedEntityType: 'inventory_item', module: 'finance',
    ...baseMeta({ createdAt: minutesAgo(36), createdBy: 'finance_agent' }),
  },
  // 4) CustomerAgent weighs in on SLA impact
  {
    id: 'evt-004', eventId: 'evt-004', threadId: STOCKOUT_THREAD, stepIndex: 3,
    kind: 'observation',
    fromAgentId: 'customer_agent', toAgentId: null,
    topic: 'Customer SLA risk',
    payload: { customer: 'Wild Fork Foods', slaAtRisk: true, impactScore: 0.82 },
    confidence: 0.86,
    rationale: 'Wild Fork Foods depends on SKU-4421 for their Tuesday allocation. SLA is already at 89% (target 95%). A stockout breach is material.',
    relatedEntityId: 'cu-005', relatedEntityType: 'customer', module: 'cx',
    ...baseMeta({ createdAt: minutesAgo(32), createdBy: 'customer_agent' }),
  },
  // 5) ComplianceAgent checks and clears
  {
    id: 'evt-005', eventId: 'evt-005', threadId: STOCKOUT_THREAD, stepIndex: 4,
    kind: 'observation',
    fromAgentId: 'compliance_agent', toAgentId: null,
    topic: 'Compliance clearance',
    payload: { hazmat: false, coldChain: false, carrierHazmatValid: true },
    confidence: 1.0,
    rationale: 'SKU-4421 is non-hazmat, non-cold-chain. No compliance veto. UPS Air carrier credentials are current.',
    relatedEntityId: 'SKU-4421', relatedEntityType: 'inventory_item', module: 'compliance',
    ...baseMeta({ createdAt: minutesAgo(30), createdBy: 'compliance_agent' }),
  },
  // 6) InventoryAgent accepts the split
  {
    id: 'evt-006', eventId: 'evt-006', threadId: STOCKOUT_THREAD, stepIndex: 5,
    kind: 'counter_proposal',
    fromAgentId: 'inventory_agent', toAgentId: 'finance_agent',
    topic: 'Split accept',
    payload: { accept: 'partial_expedite', recommendedQty: 240, note: 'Covers 10 days of demand; bridges to standard-ship replenishment.' },
    confidence: 0.87,
    rationale: 'Splitting works if expedited portion is at least 240 units — 200 leaves a 2-day gap we cannot cover.',
    relatedEntityId: 'SKU-4421', relatedEntityType: 'inventory_item', module: 'ims',
    ...baseMeta({ createdAt: minutesAgo(25), createdBy: 'inventory_agent' }),
  },
  // 7) CarrierAgent reconfirms at the new quantity
  {
    id: 'evt-007', eventId: 'evt-007', threadId: STOCKOUT_THREAD, stepIndex: 6,
    kind: 'proposal',
    fromAgentId: 'carrier_agent', toAgentId: null,
    topic: 'Carrier reconfirms',
    payload: { carrier: 'UPS Air', quantity: 240, estCost: 4100, leadTime: 2 },
    confidence: 0.89,
    rationale: 'UPS Air confirms 240-unit expedited shipment at $4,100. Remaining 240 will standard-ship at $620.',
    relatedEntityId: 'SKU-4421', relatedEntityType: 'inventory_item', module: 'tms',
    ...baseMeta({ createdAt: minutesAgo(20), createdBy: 'carrier_agent' }),
  },
  // 8) Consensus
  {
    id: 'evt-008', eventId: 'evt-008', threadId: STOCKOUT_THREAD, stepIndex: 7,
    kind: 'consensus',
    fromAgentId: 'swarm', toAgentId: null,
    topic: 'Split expedite + standard replenishment',
    payload: {
      decision: 'split_replenishment',
      expedite: { quantity: 240, carrier: 'UPS Air', estCost: 4100, eta: '2 days' },
      standard: { quantity: 240, carrier: 'Ground', estCost: 620, eta: '8 days' },
      totalCost: 4720,
      withinBudget: true,
      contributingAgents: ['inventory_agent', 'carrier_agent', 'finance_agent', 'customer_agent', 'compliance_agent'],
    },
    confidence: 0.88,
    rationale: 'Unanimous consensus: expedite 240 units via UPS Air ($4,100) and standard-ship the remaining 240 ($620). Total $4,720 — within remaining expedite budget and protects Wild Fork Foods SLA.',
    relatedEntityId: 'SKU-4421', relatedEntityType: 'inventory_item', module: 'aegis',
    ...baseMeta({ createdAt: minutesAgo(15), createdBy: 'swarm' }),
  },
];

// ── Also seed an in-progress and an escalated negotiation ──────────────
const HOS_THREAD = 'neg-002';
const COLD_CHAIN_THREAD = 'neg-003';

SEED_AEGIS_AGENT_EVENTS.push(
  // HOS vs customer ETA (in progress)
  {
    id: 'evt-101', eventId: 'evt-101', threadId: HOS_THREAD, stepIndex: 0,
    kind: 'observation',
    fromAgentId: 'fleet_agent', toAgentId: null,
    topic: 'Driver HOS tight on WC-10703',
    payload: { driver: 'Marcus Delgado', hosAvailable: 2.3, route: 'DAL-LAX', etaSlipHours: 2 },
    confidence: 0.82,
    rationale: 'Driver HOS runs out before scheduled delivery. Options: reset near Flagstaff, swap driver, or swap carrier.',
    relatedEntityId: 'load-00003', relatedEntityType: 'load', module: 'fleet',
    ...baseMeta({ createdAt: minutesAgo(18), createdBy: 'fleet_agent' }),
  },
  {
    id: 'evt-102', eventId: 'evt-102', threadId: HOS_THREAD, stepIndex: 1,
    kind: 'proposal',
    fromAgentId: 'customer_agent', toAgentId: 'fleet_agent',
    topic: 'Protect on-time delivery',
    payload: { action: 'proactive_eta_update', also: 'swap_driver_if_possible' },
    confidence: 0.87,
    rationale: 'Customer expects delivery by Tuesday 08:00. Recommend proactive ETA update now; swap drivers if a compliant option exists within 60mi.',
    relatedEntityId: 'load-00003', relatedEntityType: 'load', module: 'cx',
    ...baseMeta({ createdAt: minutesAgo(14), createdBy: 'customer_agent' }),
  },

  // Cold chain escalated — compliance vetoed expedite
  {
    id: 'evt-201', eventId: 'evt-201', threadId: COLD_CHAIN_THREAD, stepIndex: 0,
    kind: 'observation',
    fromAgentId: 'inventory_agent', toAgentId: null,
    topic: 'Reefer SKU stockout risk',
    payload: { sku: 'SKU-9910', projectedStockout: '3 days', category: 'frozen_meats' },
    confidence: 0.9,
    rationale: 'SKU-9910 is a cold-chain SKU — stockout imminent.',
    relatedEntityId: 'SKU-9910', relatedEntityType: 'inventory_item', module: 'ims',
    ...baseMeta({ createdAt: hoursAgo(3), createdBy: 'inventory_agent' }),
  },
  {
    id: 'evt-202', eventId: 'evt-202', threadId: COLD_CHAIN_THREAD, stepIndex: 1,
    kind: 'proposal',
    fromAgentId: 'carrier_agent', toAgentId: null,
    topic: 'Expedited reefer truck',
    payload: { carrier: 'FrozenFast', equipment: 'Reefer 53ft' },
    confidence: 0.76,
    rationale: 'FrozenFast is the fastest reefer option available tonight.',
    relatedEntityId: 'SKU-9910', relatedEntityType: 'inventory_item', module: 'tms',
    ...baseMeta({ createdAt: hoursAgo(3), createdBy: 'carrier_agent' }),
  },
  {
    id: 'evt-203', eventId: 'evt-203', threadId: COLD_CHAIN_THREAD, stepIndex: 2,
    kind: 'escalation',
    fromAgentId: 'compliance_agent', toAgentId: null,
    topic: 'VETO — carrier cold-chain creds expired',
    payload: { carrier: 'FrozenFast', issue: 'cold_chain_certification_expired_45d' },
    confidence: 1.0,
    rationale: 'FrozenFast\'s cold-chain certification expired 45 days ago. Veto on this carrier. Escalating to human — need alternate carrier or longer lead time.',
    relatedEntityId: 'SKU-9910', relatedEntityType: 'inventory_item', module: 'compliance',
    ...baseMeta({ createdAt: hoursAgo(2.5), createdBy: 'compliance_agent' }),
  },
);

// ── Negotiation thread records ─────────────────────────────────────────
export const SEED_AEGIS_NEGOTIATIONS = [
  {
    id: 'neg-001', negotiationId: STOCKOUT_THREAD,
    topic: 'SKU-4421 stockout risk · Wild Fork Foods',
    module: 'ims',
    status: 'consensus',
    participants: ['inventory_agent', 'carrier_agent', 'finance_agent', 'customer_agent', 'compliance_agent'],
    rounds: 3,
    startedAt: minutesAgo(45),
    resolvedAt: minutesAgo(15),
    consensus: {
      summary: 'Split replenishment: expedite 240u via UPS Air ($4,100) + standard-ship 240u ($620). Total $4,720.',
      recommendation: 'split_replenishment',
      confidence: 0.88,
      agentVotes: {
        inventory_agent: { vote: 'approve', confidence: 0.87 },
        carrier_agent: { vote: 'approve', confidence: 0.89 },
        finance_agent: { vote: 'approve', confidence: 0.88 },
        customer_agent: { vote: 'approve', confidence: 0.86 },
        compliance_agent: { vote: 'approve', confidence: 1.0 },
      },
    },
    relatedEntityId: 'SKU-4421', relatedEntityType: 'inventory_item',
    transcript: ['evt-001', 'evt-002', 'evt-003', 'evt-004', 'evt-005', 'evt-006', 'evt-007', 'evt-008'],
    ...baseMeta({ createdAt: minutesAgo(45), updatedAt: minutesAgo(15) }),
  },
  {
    id: 'neg-002', negotiationId: HOS_THREAD,
    topic: 'Driver HOS pressure on WC-10703',
    module: 'fleet',
    status: 'open',
    participants: ['fleet_agent', 'customer_agent', 'carrier_agent'],
    rounds: 1,
    startedAt: minutesAgo(18),
    resolvedAt: null,
    consensus: null,
    relatedEntityId: 'load-00003', relatedEntityType: 'load',
    transcript: ['evt-101', 'evt-102'],
    ...baseMeta({ createdAt: minutesAgo(18), updatedAt: minutesAgo(14) }),
  },
  {
    id: 'neg-003', negotiationId: COLD_CHAIN_THREAD,
    topic: 'Cold-chain SKU expedite blocked by compliance',
    module: 'ims',
    status: 'escalated',
    participants: ['inventory_agent', 'carrier_agent', 'compliance_agent'],
    rounds: 2,
    startedAt: hoursAgo(3),
    resolvedAt: null,
    escalatedTo: 'user-001',
    escalatedAt: hoursAgo(2.5),
    escalationReason: 'ComplianceAgent veto — carrier cold-chain certification expired. Need human decision on alternate carrier or timeline.',
    consensus: null,
    relatedEntityId: 'SKU-9910', relatedEntityType: 'inventory_item',
    transcript: ['evt-201', 'evt-202', 'evt-203'],
    ...baseMeta({ createdAt: hoursAgo(3), updatedAt: hoursAgo(2.5) }),
  },
];

export const SWARM_SEED_SUMMARY = {
  aegis_agents: SEED_AEGIS_AGENTS.length,
  aegis_agent_memory: SEED_AGENT_MEMORY.length,
  aegis_agent_events: SEED_AEGIS_AGENT_EVENTS.length,
  aegis_negotiations: SEED_AEGIS_NEGOTIATIONS.length,
};
